/**
 * @voltxampere/presets — Voltage divider (#1)
 *
 * Resistive divider — V(mid) = V1 · R2/(R1+R2). The canonical
 * "first DC analysis" demo: zero nonlinear elements, exact
 * analytical solution that agrees with the engine to machine
 * epsilon (frozen-regression sentinel V_mid = 5.0).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
// Sprint 25 R2 lane 4D-A — hand-authored layout. Body centers
// rounded to 20-px grid. Pin world coords are derived by the
// builder from `position + rotation` via the pin-local table in
// `_layout-helpers.ts`. Preset wires are explicit Point[] paths.
export const voltageDivider = {
    id: "voltage-divider",
    name: { tr: "Gerilim Bölücü", en: "Voltage Divider" },
    category: "basic",
    difficulty: 1,
    description: {
        tr: "İki direnç ile gerilim bölme. V1=10V, R1=R2=1kΩ → V(mid)=5V.",
        en: "Two-resistor voltage divider. V1=10V, R1=R2=1kΩ → V(mid)=5V.",
    },
    circuit: makeCircuit({
        slot: 1,
        name: "Voltage Divider",
        description: "Two-resistor voltage divider",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vcc" }),
            makeNet({ name: "mid" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 10 },
                terminals: { pos: "net_vcc", neg: "net_gnd" },
                label: "V1",
                position: { x: -160, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_vcc", p2: "net_mid" },
                label: "R1",
                position: { x: 0, y: -60 },
                rotation: 90,
            }),
            makeComponent({
                id: "R2",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_mid", p2: "net_gnd" },
                label: "R2",
                position: { x: 0, y: 60 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -160, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            // vcc rail: V1.pos (-160, -10) → R1.p1 (0, -80)
            {
                id: "w_vcc",
                netId: "net_vcc",
                path: [
                    { x: -160, y: -10 },
                    { x: -160, y: -80 },
                    { x: 0, y: -80 },
                ],
                style: "orthogonal",
            },
            // mid rail: R1.p2 (0, -40) → R2.p1 (0, 40)
            {
                id: "w_mid",
                netId: "net_mid",
                path: [
                    { x: 0, y: -40 },
                    { x: 0, y: 40 },
                ],
                style: "orthogonal",
            },
            // gnd rail leg A: V1.neg (-160, 10) → GND.p (-160, 71)
            {
                id: "w_gnd_left",
                netId: "net_gnd",
                path: [
                    { x: -160, y: 10 },
                    { x: -160, y: 71 },
                ],
                style: "orthogonal",
            },
            // gnd rail leg B: R2.p2 (0, 80) → GND.p (-160, 71)
            {
                id: "w_gnd_right",
                netId: "net_gnd",
                path: [
                    { x: 0, y: 80 },
                    { x: 0, y: 110 },
                    { x: -160, y: 110 },
                    { x: -160, y: 71 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "mid",
            analysis: "dc",
            value: 5,
            tolerance: 0.001,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Direnç oranıyla gerilim ölçeklendirme: V_mid = V1·R2/(R1+R2).",
            en: "Voltage scaling by resistor ratio: V_mid = V1·R2/(R1+R2).",
        },
        {
            tr: "Topraklanmış referans noktası ve KCL/KVL temel doğrulaması.",
            en: "Grounded reference and basic KCL/KVL verification.",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 1"],
};
//# sourceMappingURL=voltage-divider.js.map